import { SchemaForm } from "components/Form/components/SchemaForm/SchemaForm";
import { Scheduler } from "graphql/generated/graphql";
import { useContext, useMemo } from "react";
import { RouterContext } from "utils/RouterManager/RouterManager";

interface SchedulerInputSchemaTabProps {
    activeTab: string;
    resourceData: Scheduler;
}

/**
 * Read-only view of the FIXED input envelope a SCHEDULE execution receives.
 *
 * When a `SCHEDULE` record fires it creates an execution of the scheduler flow
 * with a platform-hardcoded `input_schema` (see backend
 * `resources/execution.py` — `_mutate_body_insert_handle_type_schedule_load`):
 * `{flow_id, schedule_id, configuration}`, all required, where the
 * `configuration` sub-schema is the scheduler's own author-defined
 * `configuration_schema`.
 *
 * The scheduler author does NOT define this envelope (they only define the
 * `configuration_schema` shape, edited in the "Configuration schema" tab), so it
 * is shown read-only here — mirroring the fixed contract in the source so the
 * author can see exactly what their scheduler flow is invoked with. There is
 * deliberately no "stamp" affordance: the envelope is fixed by the platform, not
 * writable per flow (Stefan, operator-task-stamp-input-schema-webhook-scheduler-contexts).
 */
export const SchedulerInputSchemaTab = ({
    activeTab,
    resourceData,
}: SchedulerInputSchemaTabProps) => {
    // The meta-schema describing the schema-form-builder structure (same one the
    // flow/scheduler editable schema tabs use), so the envelope renders in the
    // familiar Form/Code builder view — always read-only here.
    const schemaFormSchema = useContext(RouterContext).filteredSchemaFormSchema;

    const value = useMemo(() => {
        const configurationSchema =
            (resourceData as any)?.configuration_schema ?? {};
        // Mirror resources/execution.py: the fixed schedule-execution envelope.
        const envelopeSchema = {
            element: "form-object",
            type: "object",
            additionalProperties: false,
            label: "Schedule execution",
            properties: {
                flow_id: {
                    element: "form-autocomplete",
                    type: "string",
                    record_type: "FLOW",
                    object_template_id: null,
                    default: "",
                    label: "Flow",
                },
                schedule_id: {
                    element: "form-autocomplete",
                    type: "string",
                    record_type: "SCHEDULE",
                    object_template_id: null,
                    default: "",
                    label: "Schedule",
                },
                configuration: configurationSchema,
            },
            order: ["flow_id", "schedule_id", "configuration"],
            required: ["flow_id", "schedule_id", "configuration"],
        };
        return { values: envelopeSchema, schema: schemaFormSchema };
    }, [resourceData, schemaFormSchema]);

    return (
        <div key={`${activeTab}-schedulerInputSchema`} className="schemaFormTab">
            <SchemaForm inputProps={{ value, isReadOnly: true }} />
        </div>
    );
};
